/**
 * net.js — Networking client for Sky-Drop online multiplayer.
 *
 * Thin wrapper over the FastAPI backend: REST for room lifecycle
 * (create/join/list/get) and a WebSocket for real-time, room-wide message
 * relay. This module is pure transport — it knows nothing about game physics or
 * rendering. Higher layers (lobby UI, game-loop sync) build on top of it.
 *
 * Backend location comes from REACT_APP_BACKEND_URL at build time, defaulting
 * to http://localhost:8001 for local development. See README for deployment.
 */

// Base HTTP origin of the backend. Trailing slashes are trimmed so we can
// safely template `${BASE}/api/...`.
const BASE = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001').replace(/\/+$/, '');

/** @returns {string} the API root, e.g. "http://localhost:8001/api". */
function apiRoot() {
  return `${BASE}/api`;
}

/**
 * Derive the WebSocket origin from the HTTP base: http→ws, https→wss.
 * @returns {string} e.g. "ws://localhost:8001".
 */
function wsRoot() {
  if (BASE.startsWith('https://')) return `wss://${BASE.slice('https://'.length)}`;
  if (BASE.startsWith('http://')) return `ws://${BASE.slice('http://'.length)}`;
  // Bare host: fall back to insecure ws.
  return `ws://${BASE}`;
}

/**
 * POST/GET helper that parses JSON and throws a useful Error on non-2xx.
 * @param {string} url
 * @param {object} [options] fetch options.
 * @returns {Promise<object>} parsed JSON body.
 */
async function request(url, options = {}) {
  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!res.ok) {
    // Surface the backend's { detail } message when present.
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail || detail;
    } catch (err) {
      /* non-JSON error body — keep statusText */
    }
    throw new Error(`${res.status}: ${detail}`);
  }
  return res.json();
}

/**
 * Create a new game room.
 * @param {string} playerName
 * @param {'online'|'ai'} [gameMode]
 * @param {'easy'|'medium'|'hard'} [difficulty]
 * @returns {Promise<{roomId: string, playerId: string, room: object}>}
 */
export function createRoom(playerName, gameMode = 'online', difficulty = 'medium') {
  return request(`${apiRoot()}/rooms`, {
    method: 'POST',
    body: JSON.stringify({ playerName, gameMode, difficulty }),
  });
}

/**
 * Join an existing room by its short code.
 * @param {string} roomId
 * @param {string} playerName
 * @returns {Promise<{roomId: string, playerId: string, room: object}>}
 */
export function joinRoom(roomId, playerName) {
  return request(`${apiRoot()}/rooms/${roomId}/join`, {
    method: 'POST',
    body: JSON.stringify({ roomId, playerName }),
  });
}

/**
 * List rooms that are waiting for another player.
 * @returns {Promise<{rooms: Array<object>}>}
 */
export function listRooms() {
  return request(`${apiRoot()}/rooms`);
}

/**
 * Fetch a single room's full state.
 * @param {string} roomId
 * @returns {Promise<object>}
 */
export function getRoom(roomId) {
  return request(`${apiRoot()}/rooms/${roomId}`);
}

/**
 * Open a WebSocket to a room and wire up event callbacks.
 *
 * Returns a small connection handle rather than the raw socket so callers have
 * a stable, intent-revealing API (send/close) and never touch JSON plumbing.
 * All outgoing/incoming payloads are JSON objects.
 *
 * @param {string} roomId
 * @param {string} playerId
 * @param {object} handlers
 * @param {(msg: object) => void} [handlers.onMessage] Parsed inbound message.
 * @param {() => void} [handlers.onOpen]
 * @param {() => void} [handlers.onClose]
 * @param {(err: Event) => void} [handlers.onError]
 * @returns {{ send: (msg: object) => void, close: () => void, raw: WebSocket }}
 */
export function connectToRoom(roomId, playerId, handlers = {}) {
  const ws = new WebSocket(`${wsRoot()}/api/ws/${roomId}/${playerId}`);

  // Kept mutable so callers can hand off the message stream after the lobby
  // handshake (e.g. from lobby logic to the in-game sync handler) without
  // reopening the socket. See setMessageHandler below.
  let messageHandler = handlers.onMessage;

  ws.onopen = () => handlers.onOpen && handlers.onOpen();
  ws.onclose = () => handlers.onClose && handlers.onClose();
  ws.onerror = (err) => handlers.onError && handlers.onError(err);
  ws.onmessage = (event) => {
    if (!messageHandler) return;
    try {
      messageHandler(JSON.parse(event.data));
    } catch (err) {
      // Ignore malformed frames rather than crashing the message pump.
      console.warn('Dropped malformed WS message:', err);
    }
  };

  return {
    /** Send a JSON-serialisable message if the socket is open. */
    send(msg) {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(msg));
      }
    },
    /** Replace the inbound-message handler (parsed JSON object in). */
    setMessageHandler(fn) {
      messageHandler = fn;
    },
    /** Close the connection. */
    close() {
      ws.close();
    },
    /** Escape hatch to the underlying socket. */
    raw: ws,
  };
}
